import { type ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { ListableInfo, LoadingIndicator, Panel } from '@ics-portal/guardian';
import { formatDate } from '@utils/dateFormatUtils';
import { VendorAssessment } from '@components/VendorAssessment/VendorAssessment';
import { ExternalIdentifiers } from '@components/ExternalIdentifiers/ExternalIdentifiers';
import { InfoCardWrapper } from '@components/_molecules/InfoCards';
import { ExternalSourcesPanel } from '@components/ExternalSourcesPanel';
import { GET_COMPONENT_VENDOR_AND_IDENTIFIERS } from '@api/queries/vendor';
import { type ComponentDetailsViewModel } from './models/ComponentDetailsViewModel';

interface ComponentDetailsCardProps {
    data: ComponentDetailsViewModel;
    sourcesLoading?: boolean;
}

function renderExternalLink(url: string | null | undefined): ReactNode {
    if (!url) return '-';
    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{ wordBreak: 'break-all' }}
        >
            {url}
        </a>
    );
}

export function ComponentDetailsCard({
    data,
    sourcesLoading,
}: ComponentDetailsCardProps): ReactNode {
    const { data: vendorAndIdentifiersData, loading: vendorLoading } = useQuery(
        GET_COMPONENT_VENDOR_AND_IDENTIFIERS,
        {
            variables: { componentId: data.id },
        },
    );

    const componentNode = vendorAndIdentifiersData?.components.edges[0]?.node;

    return (
        <InfoCardWrapper title="Details">
            <Panel header="Component">
                <div className="flex flex-column gap-2 pt-sm pb-lg">
                    <ListableInfo size="md" label="Name" value={data.name} />
                    <ListableInfo
                        size="md"
                        label="Version"
                        value={data.version}
                    />
                    <ListableInfo
                        size="md"
                        label="EOL date"
                        value={data.eolDate ? formatDate(data.eolDate) : '-'}
                    />
                    <ListableInfo
                        size="md"
                        label="EOL reached"
                        value={data.eolReached ? 'Yes' : 'No'}
                    />
                    <ListableInfo
                        size="md"
                        label="Is valid"
                        value={data.isValid ? 'True' : 'False'}
                    />
                    <ListableInfo
                        size="md"
                        label="Monitored since"
                        value={
                            data.monitoredSince
                                ? formatDate(data.monitoredSince)
                                : '-'
                        }
                    />
                    <ListableInfo
                        size="md"
                        label="Vendor"
                        value={data.vendorName ?? '-'}
                    />
                    <ListableInfo
                        size="md"
                        label="URL"
                        value={renderExternalLink(data.url)}
                    />
                    <ListableInfo
                        size="md"
                        label="Security URL"
                        value={renderExternalLink(data.securityUrl)}
                    />
                </div>
            </Panel>
            {vendorLoading ? (
                <LoadingIndicator variant="ring" />
            ) : (
                <VendorAssessment
                    vendorName={componentNode?.componentVendor.name}
                    assessments={componentNode?.componentVendor.assessments}
                />
            )}
            <Panel header="External identifiers">
                {vendorLoading ? (
                    <LoadingIndicator variant="ring" />
                ) : (
                    <ExternalIdentifiers
                        externalIdentifiers={
                            componentNode?.componentExternalIdentifiers
                        }
                        icsPortalSource={data.cpeName}
                    />
                )}
            </Panel>
            <ExternalSourcesPanel
                sourcesData={data.sourcesData}
                loading={sourcesLoading}
            />
        </InfoCardWrapper>
    );
}
